import { useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  selectBuildingsForFilter,
  selectActiveBuilding,
} from '../../reducers/buildingSelectorReducer'
import Can from '../Can'
import API from '../../api/api'
import Icon from '../Icons'
import SearchField from '../FormLegacy/FormFieldsV2/SearchField'
import Filter from '../Filter/Filter'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'

import IntegrationCard from './components/IntegrationCard'
import IntegrationModal from './components/IntegrationModal'
import RequestIntegrationModal from './components/RequestIntegrationModal'
import {
  INTEGRATION_CATEGORIES,
  getIntegrationStatusOptions,
  getCategoryLabels,
  getIntegrationsData,
} from './constants'

const Integrations = ({ buildings, activeBuilding }) => {
  const { t } = useTranslation(['integrations'])
  const INTEGRATION_STATUS_OPTIONS = getIntegrationStatusOptions(t)
  const CATEGORY_LABELS = getCategoryLabels(t)
  const INTEGRATIONS_DATA = getIntegrationsData(t)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedStatus, setSelectedStatus] = useState(INTEGRATION_STATUS_OPTIONS[0])
  const [selectedIntegration, setSelectedIntegration] = useState(null)
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isRequestSent, setIsRequestSent] = useState(false)
  const { displaySnackbar } = useSnackbar()

  const handleCloseRequestModal = () => {
    setSelectedIntegration(null)
    setIsRequestModalOpen(false)
    setIsRequestSent(false)
  }

  const handleRequestIntegration = async (requestData) => {
    try {
      setIsLoading(true)
      const targetBuildingId = activeBuilding?.id || buildings?.[0]?.id

      if (!targetBuildingId) {
        console.error('Unable to send integration request: no building selected')
        return
      }

      await API.sendUpsellingMessage({ type: 'integrations', ...requestData }, targetBuildingId)
      setIsRequestModalOpen(false)
      setIsRequestSent(true)
      displaySnackbar(t('integrations:toasts.requestSuccess'))
    } catch (error) {
      console.error('Error sending integration request:', error.message)
      handleCloseRequestModal()
      displaySnackbar(t('integrations:toasts.requestError'), true)
    } finally {
      setIsLoading(false)
    }
  }

  const filteredIntegrations = INTEGRATIONS_DATA.filter((integration) => {
    const matchesSearch =
      integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'All' || integration.category === selectedCategory
    const matchesStatus =
      !selectedStatus.value ||
      selectedStatus.value === 'All' ||
      integration.status === selectedStatus.value

    return matchesSearch && matchesCategory && matchesStatus
  })

  return (
    <Can to="integrationsPM" on="Navbar">
      <div className="integrations-pm">
        <div className="integrations-pm__header">
          <h1 className="integrations-pm__title">{t('integrations:header.title')}</h1>
          <button
            className="button button-primary button-small"
            onClick={() => setIsRequestModalOpen(true)}
          >
            {t('integrations:header.requestButton')}
          </button>
        </div>

        <div className="integrations-pm__filters">
          <div className="filter-section">
            <button
              className={`filter-btn ${selectedCategory === 'All' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('All')}
            >
              {t('integrations:shared.allOption')}
            </button>
            {INTEGRATION_CATEGORIES.map((category) => (
              <button
                key={category}
                className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === 'Packages'
                  ? t('integrations:categories.packagePartners')
                  : CATEGORY_LABELS[category] || category}
              </button>
            ))}
          </div>

          <div className="right-filters">
            <div className="status-filter">
              <Filter
                placeholder={selectedStatus.text || t('integrations:filters.statusPlaceholder')}
                options={INTEGRATION_STATUS_OPTIONS}
                value={selectedStatus ? [selectedStatus] : [INTEGRATION_STATUS_OPTIONS[0]]}
                name="status"
                onChange={(e) => setSelectedStatus(e.target.value)}
                closeAfterSelect
                config={{ multiple: false }}
              />
            </div>
            <div className="search-filter">
              <SearchField
                placeholder={t('integrations:filters.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="integrations-pm__grid">
          {filteredIntegrations.length > 0 ? (
            filteredIntegrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onClick={() => {
                  setSelectedIntegration(integration)
                  setIsRequestSent(false)
                }}
              />
            ))
          ) : (
            <div className="integrations-pm__empty-state">
              <div className="integrations-pm__empty-state__icon">
                <Icon icon="search" width="48" height="48" />
              </div>
              <h3 className="integrations-pm__empty-state__title">
                {t('integrations:emptyState.title')}
              </h3>
              <p className="integrations-pm__empty-state__description">
                {searchTerm
                  ? t('integrations:emptyState.noMatchSearchDescription', { searchTerm })
                  : t('integrations:emptyState.noMatchFiltersDescription')}
              </p>
              <button
                className="button button-secondary"
                onClick={() => {
                  setSearchTerm('')
                  setSelectedCategory('All')
                  setSelectedStatus(INTEGRATION_STATUS_OPTIONS[0])
                }}
              >
                {t('integrations:emptyState.clearFiltersButton')}
              </button>
            </div>
          )}
        </div>

        {selectedIntegration && (
          <IntegrationModal
            integration={selectedIntegration}
            onClose={handleCloseRequestModal}
            onRequest={(data) =>
              handleRequestIntegration({
                ...data,
                integrationName: selectedIntegration.name,
              })
            }
            isLoading={isLoading}
            isRequestSent={isRequestSent}
          />
        )}

        {isRequestModalOpen && (
          <RequestIntegrationModal
            onClose={handleCloseRequestModal}
            onSubmit={handleRequestIntegration}
            isLoading={isLoading}
            isRequestSent={isRequestSent}
          />
        )}
      </div>
    </Can>
  )
}

export default connect((state) => ({
  buildings: selectBuildingsForFilter(state),
  activeBuilding: selectActiveBuilding(state),
}))(Integrations)
